import { Slot } from "@radix-ui/react-slot";
import React from "react";

import { cn } from "@/lib/cn";

import { headingVariants } from "./Heading";

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
  variant?: "default" | "outlined";
  ref?: React.Ref<HTMLDivElement>;
}

const Card = ({ className, asChild = false, variant = "default", ref, ...props }: CardProps) => {
  const Component = asChild ? Slot : "div";
  return (
    <Component
      ref={ref}
      className={cn(
        "rounded-lg bg-card text-card-foreground ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        variant === "outlined" ? "border border-border" : "shadow-xs",
        className,
      )}
      {...props}
    />
  );
};

Card.displayName = "Card";

const CardHeader = ({ className, ref, ...props }: CardProps) => (
  <div ref={ref} className={cn("flex flex-col space-y-1.5 p-6", className)} {...props} />
);
CardHeader.displayName = "CardHeader";

interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  ref?: React.Ref<HTMLHeadingElement>;
  /** `title` for a card stacked with others in one view; a Headline by default */
  variant?: "title";
}

// h2: a card is the first level below a view's h1
const CardTitle = ({ className, variant, ref, children, ...props }: CardTitleProps) => (
  <h2
    ref={ref}
    className={cn(
      variant ? headingVariants({ variant }) : "text-2xl font-medium leading-none tracking-tight",
      className,
    )}
    {...props}
  >
    {children}
  </h2>
);
CardTitle.displayName = "CardTitle";

interface CardDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  ref?: React.Ref<HTMLParagraphElement>;
}

const CardDescription = ({ className, ref, ...props }: CardDescriptionProps) => (
  <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
);
CardDescription.displayName = "CardDescription";

const CardContent = ({ className, ref, ...props }: CardProps) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
);
CardContent.displayName = "CardContent";

const CardFooter = ({ className, ref, ...props }: CardProps) => (
  <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props} />
);
CardFooter.displayName = "CardFooter";

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
